import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Modal } from 'react-bootstrap';
import { getStorage, ref, uploadBytes, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaUpload, FaSpinner, FaEdit, FaTrash } from 'react-icons/fa';
import { getAuth } from 'firebase/auth';

const AdminGallery = () => {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('Certificate');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newImageName, setNewImageName] = useState('');
  const storage = getStorage();

  const categories = [
    'All', 'Certificate', 'Training', 'Achievements',
    'Events', 'Workshops', 'Cultural'
  ];

  useEffect(() => {
    fetchImages();
  }, [category]);

  const fetchImages = async () => {
    try {
      if (category === 'All') {
        const allImages = [];
        for (const cat of categories) {
          if (cat === 'All') continue;
          const folderRef = ref(storage, `gallery/${cat.toLowerCase()}`);
          const result = await listAll(folderRef);
          const urls = await Promise.all(
            result.items.map(async (item) => ({
              name: item.name,
              path: item.fullPath,
              url: await getDownloadURL(item),
              category: cat
            }))
          );
          allImages.push(...urls);
        }
        setImages(allImages);
      } else {
        const folderRef = ref(storage, `gallery/${category.toLowerCase()}`);
        const result = await listAll(folderRef);
        const imageUrls = await Promise.all(
          result.items.map(async (item) => ({
            name: item.name,
            path: item.fullPath,
            url: await getDownloadURL(item),
            category: category
          }))
        );
        setImages(imageUrls);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setError("Failed to load images");
    }
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    try {
      const imageRef = ref(storage, selectedImage.path);
      await deleteObject(imageRef);
      await fetchImages();
      setSuccess('Image deleted successfully');
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting image:", error);
      setError('Failed to delete image');
    }
  };

  const handleEdit = async () => {
    if (!selectedImage || !newImageName) return;

    try {
      const oldRef = ref(storage, selectedImage.path);
      const newRef = ref(storage, `gallery/${category.toLowerCase()}/${Date.now()}_${newImageName}`);
      
      // Get the image data from the old location
      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      
      // Upload to new location
      await uploadBytes(newRef, blob);
      
      // Delete from old location
      await deleteObject(oldRef);
      
      await fetchImages();
      setSuccess('Image renamed successfully');
      setShowEditModal(false);
    } catch (error) {
      console.error("Error editing image:", error);
      setError('Failed to edit image');
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    const auth = getAuth();
    if (!auth.currentUser) {
      setError('You must be logged in to upload images');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Check file sizes and types
      for (let file of files) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`File ${file.name} is too large. Maximum size is 5MB`);
        }
        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${file.name} is not an image`);
        }
      }

      // Upload files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Ensure the path matches exactly with storage rules
        const storageRef = ref(storage, `gallery/${category.toLowerCase()}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`);
        
        try {
          await uploadBytes(storageRef, file);
        } catch (uploadError) {
          console.error('Upload error details:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }
      }
      
      setSuccess(`Successfully uploaded ${files.length} image${files.length > 1 ? 's' : ''}`);
      event.target.value = null;
    } catch (error) {
      console.error("Error uploading images:", error);
      setError(error.message || 'Error uploading images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      <h2 className="mb-4">Gallery Management</h2>
      
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Category</Form.Label>
            <Form.Select 
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mb-3"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Form.Select>

            <Button
              variant="primary"
              style={{
                borderRadius: 20,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={() => document.getElementById('imageUpload').click()}
              disabled={uploading}
            >
              {uploading ? <FaSpinner className="spin" /> : <FaUpload />}
              Upload Images to {category}
            </Button>
            <Form.Control
              type="file"
              id="imageUpload"
              hidden
              multiple
              accept="image/*"
              onChange={handleImageUpload}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-4">
        {images.map((image, index) => (
          <Col key={index} xs={12} sm={6} md={4} lg={3}>
            <Card>
              <Card.Img 
                variant="top" 
                src={image.url} 
                style={{ height: '200px', objectFit: 'cover' }} 
              />
              <Card.Body>
                <Card.Title className="text-truncate">{image.name}</Card.Title>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      setSelectedImage(image);
                      setNewImageName(image.name);
                      setShowEditModal(true);
                    }}
                  >
                    <FaEdit /> Edit
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => {
                      setSelectedImage(image);
                      setShowDeleteModal(true);
                    }}
                  >
                    <FaTrash /> Delete
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this image?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Image Name</Form.Label>
            <Form.Control
              type="text"
              value={newImageName}
              onChange={(e) => setNewImageName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

export default AdminGallery;
